// Parent Homework — per-homework push-reminder control (SC2/SC3, mock: claude/hw-reminders-mock.html).
// A collapsed "🔔 Remind me…" row on each homework card (all-homework list) that expands to a slot
// panel. One row per daily repetition of the homework (its time_of_day tokens), labeled by tod
// (C1: fixed map slot 1/2/3 = morning/afternoon/evening — NO "1st/2nd/3rd practice" ordinals).
// SC3.1: the SAME panel opens as a bottom sheet over Today from a quiet per-tile bell (openReminderSheet).
// Persistence: window.HUD.saveHomeworkReminders (SC3). Nothing renders on the child-facing exercise screen.

const T = (k, p) => window.HUD?.T?.(k, p) || k;

const TOKENS = ['morning', 'afternoon', 'evening'];       // canonical order
const SLOT_OF = { morning: 1, afternoon: 2, evening: 3 }; // C1 fixed map
const DEFAULT_TIME = { morning: '08:00', afternoon: '16:00', evening: '19:00' };
const SINGLE_DEFAULT = '17:00';

function _esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

// "08:00" → "8:00" (mock chip format: no leading zero on the hour).
function _fmtTime(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  return m ? (parseInt(m[1], 10) + ':' + m[2]) : String(t || '');
}

// The homework's daily slots = unique time_of_day tokens in canonical order (defaults to morning).
function _slotsForHw(hw) {
  const toks = String(hw.time_of_day || 'morning').split(',').map(s => s.trim()).filter(Boolean);
  const uniq = TOKENS.filter(t => toks.includes(t));
  return uniq.length ? uniq : ['morning'];
}

// Saved reminders for this homework, keyed by slot number. Reads window.HUD.DB.homeworkReminders
// (the boot read carries slot + timezone since SC3). No network.
function _savedMap(hw, H) {
  const rem = (H?.DB?.homeworkReminders) || [];
  const m = {};
  rem.filter(r => r.taskId === hw.id).forEach(r => {
    if (r.slot) m[r.slot] = { time: String(r.time || '').slice(0, 5), on: r.isEnabled !== false };
  });
  return m;
}

// True when the homework has at least one enabled reminder (drives the Today tile bell tint).
export function hwHasReminders(hwId, H) {
  const rem = ((H || window.HUD)?.DB?.homeworkReminders) || [];
  return rem.some(r => r.taskId === hwId && r.isEnabled !== false);
}

// Shared interactive panel (note + slot rows + Turn-all-off + Save) + local state + persist.
// Used 1:1 by the inline card row AND the Today bottom sheet. onSaved(rows) fires after a successful save.
function _buildPanel(hw, H, onSaved) {
  const tokens = _slotsForHw(hw);
  const single = tokens.length === 1;
  const saved = _savedMap(hw, H);
  const rows = tokens.map(tok => {
    const slot = SLOT_OF[tok];
    const s = saved[slot];
    return { slot, token: tok, on: s ? s.on : false, time: (s && s.time) || (single ? SINGLE_DEFAULT : DEFAULT_TIME[tok]) };
  });

  const inner = document.createElement('div');
  inner.className = 'hwp-rem-inner';
  const slotEls = [];

  if (!single) {
    const note = document.createElement('div');
    note.className = 'hwp-rem-note';
    note.textContent = T('hw5_one_per_practice');
    inner.appendChild(note);
  }

  rows.forEach(r => {
    const sr = document.createElement('div');
    sr.className = 'hwp-rem-slot' + (r.on ? '' : ' off');

    const tgl = document.createElement('label'); tgl.className = 'hwp-tgl';
    const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = r.on;
    const knob = document.createElement('span');
    tgl.append(cb, knob);
    cb.onchange = () => { r.on = cb.checked; sr.classList.toggle('off', !cb.checked); };

    const label = document.createElement('div');
    label.className = 'hwp-rem-label';
    label.textContent = single ? T('hw5_daily_reminder') : T('hw5_slot_' + r.token);

    const time = document.createElement('input');
    time.type = 'time'; time.className = 'hwp-rem-time'; time.value = r.time;
    time.onchange = () => { r.time = time.value || r.time; };

    sr.append(tgl, label, time);
    inner.appendChild(sr);
    slotEls.push({ row: r, el: sr, checkbox: cb });
  });

  // Persist via the SC3 bridge: upsert enabled + delete toggled-off (with tz). Bridge toasts its own
  // error; we toast success and fire onSaved so the caller updates its indicator in place.
  async function _persist() {
    const payload = rows.map(r => ({ slot: r.slot, token: r.token, time: r.time, on: r.on }));
    let ok = true;
    try { const res = await window.HUD?.saveHomeworkReminders?.(hw.id, payload, hw.child_id); if (res && res.error) ok = false; } catch (_) { ok = false; }
    if (ok) { (H?.toast || window.HUD?.toast)?.(T('hw5_reminder_saved')); try { onSaved && onSaved(rows); } catch (_) {} }
  }

  const actions = document.createElement('div'); actions.className = 'hwp-rem-actions';
  if (!single) {
    const off = document.createElement('button');
    off.type = 'button'; off.className = 'hwp-rem-ghost'; off.textContent = T('hw5_turn_all_off');
    off.onclick = () => {
      rows.forEach((r, i) => { r.on = false; slotEls[i].checkbox.checked = false; slotEls[i].el.classList.add('off'); });
      _persist(); // "clears all rows for that homework" → 0 rows in DB
    };
    actions.appendChild(off);
  }
  const save = document.createElement('button');
  save.type = 'button'; save.className = 'hwp-rem-save'; save.textContent = T('hw5_save');
  save.onclick = _persist;
  actions.appendChild(save);
  inner.appendChild(actions);

  return { inner, rows };
}

// ── Inline control on the All-homework card ──────────────────────────────────
export function buildReminderRow(hw, H) {
  const cont = document.createElement('div');
  cont.className = 'hwp-rem';
  cont.addEventListener('click', e => e.stopPropagation()); // never open the board

  const rowEl = document.createElement('div');
  rowEl.className = 'hwp-rem-row';
  const bell = document.createElement('span'); bell.className = 'hwp-rem-bell'; bell.textContent = '🔔';
  const summary = document.createElement('span'); summary.className = 'hwp-rem-summary';
  const chev = document.createElement('span'); chev.className = 'hwp-rem-chev'; chev.textContent = '▼';
  rowEl.append(bell, summary, chev);
  rowEl.onclick = () => cont.classList.toggle('open');
  cont.appendChild(rowEl);

  const panel = document.createElement('div'); panel.className = 'hwp-rem-panel';
  const built = _buildPanel(hw, H, () => _renderSummary());
  panel.appendChild(built.inner);
  cont.appendChild(panel);

  function _renderSummary() {
    const on = built.rows.filter(r => r.on).slice().sort((a, b) => a.slot - b.slot);
    if (on.length) {
      bell.style.filter = ''; bell.style.opacity = '';
      summary.className = 'hwp-rem-summary set';
      summary.innerHTML = _esc(T('hw5_reminders')) + ' <span class="hwp-rem-chips">' +
        on.map(r => `<span class="hwp-rem-chip">${_esc(_fmtTime(r.time))}</span>`).join('') + '</span>';
    } else {
      bell.style.filter = 'grayscale(1)'; bell.style.opacity = '.6';
      summary.className = 'hwp-rem-summary';
      summary.textContent = T('hw5_remind_me');
    }
  }
  _renderSummary();

  return cont;
}

// ── Bottom sheet over Today (SC3.1) — same panel, same save path ─────────────
// onSaved() fires after a successful save (caller updates the tile bells in place). Dismiss on
// tap-outside / swipe-down; dismiss without Save changes nothing. Mounted on document.body.
export function openReminderSheet(hw, H, onSaved) {
  const back = document.createElement('div'); back.className = 'hwp-rem-sheet-back';
  const sheet = document.createElement('div'); sheet.className = 'hwp-rem-sheet';
  const grip = document.createElement('div'); grip.className = 'hwp-rem-grip';
  const built = _buildPanel(hw, H, () => { try { onSaved && onSaved(); } catch (_) {} });
  sheet.append(grip, built.inner);
  back.appendChild(sheet);
  document.body.appendChild(back);
  requestAnimationFrame(() => back.classList.add('open'));

  let closed = false;
  function close() {
    if (closed) return; closed = true;
    back.classList.remove('open');
    setTimeout(() => back.remove(), 240);
  }
  back.addEventListener('click', e => { if (e.target === back) close(); }); // tap-outside

  // swipe-down to dismiss
  let sy = 0, dy = 0, dragging = false;
  sheet.addEventListener('touchstart', e => { sy = e.touches[0].clientY; dy = 0; dragging = true; }, { passive: true });
  sheet.addEventListener('touchmove', e => {
    if (!dragging) return;
    dy = e.touches[0].clientY - sy;
    if (dy > 0) sheet.style.transform = 'translateY(' + dy + 'px)';
  }, { passive: true });
  sheet.addEventListener('touchend', () => {
    dragging = false;
    if (dy > 90) close(); else sheet.style.transform = '';
  });

  return { close };
}
